// Standard (default) logbook PDF layout. 
import { ENABLE_ASSESSMENT } from "@/config/featureFlags";
import {
    createLogbookDoc,
    drawHeader,
    drawFooter,
    savePdf,
    INFO_TABLE_STYLE,
    INFO_TABLE_HEAD_STYLE,
    groupTasksByChapter,
    sortChapterNumbers,
    getChapterName,
} from "./helpers";

function capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function formatEvaluationDate(dateValue) {
    if (!dateValue) return '';
    const evalDate = new Date(dateValue);
    const day = String(evalDate.getDate()).padStart(2, '0');
    const month = String(evalDate.getMonth() + 1).padStart(2, '0');
    const year = evalDate.getFullYear();
    return `${day}/${month}/${year}`;
}

function getEvaluationDisplayValue(evaluation, criterionKey) {
    if (!evaluation || !evaluation.criteria) return 'Not yet Evaluated';
    return capitalizeFirstLetter(evaluation.criteria?.[criterionKey] || 'Not yet Evaluated');
}

function getEvaluationResultText(evaluation) {
    if (!evaluation || !evaluation.result) return 'Not yet Evaluated';
    return evaluation.result.toUpperCase();
}

function getEvaluationDateText(evaluation) {
    return evaluation?.date ? formatEvaluationDate(evaluation.date) : 'N/A';
}

function addEvaluationSection(doc, evaluation, title, context) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`${title}:`, 10, 10);

    doc.setFont('helvetica', 'normal');

    const evaluationColumns = ['Competence Criteria', 'Performance Result'];

    const evaluationTableData = [
        [
        { content: 'Consults the correct documentation' },
        { content: getEvaluationDisplayValue(evaluation, '1') },
        ],
        [
        { content: 'Scans the environment before starting and during the task to ensure safety' },
        { content: getEvaluationDisplayValue(evaluation, '2') },
        ],
        [
        { content: 'Reads/interprets the safety warning correctly' },
        { content: getEvaluationDisplayValue(evaluation, '3') },
        ],
        [
        { content: 'Informs/communicates with appropriate people correctly' },
        { content: getEvaluationDisplayValue(evaluation, '4') },
        ],
        [
        { content: 'Interprets the reports, indications and documentation correctly' },
        { content: getEvaluationDisplayValue(evaluation, '5') },
        ],
        [
        { content: 'Follows the procedure steps' },
        { content: getEvaluationDisplayValue(evaluation, '6') },
        ],
        [
        { content: 'Takes into account interactions between systems' },
        { content: getEvaluationDisplayValue(evaluation, '7') },
        ],
        [
        { content: 'Restores the aircraft back to initial condition (or appropriate condition depending on the circumstances)' },
        { content: getEvaluationDisplayValue(evaluation, '8') },
        ],
        [
        {
            content: 'RESULT OF THE ASSESSMENT',
            styles: { fillColor: [211, 211, 211], fontStyle: 'bold', lineColor: [0, 0, 0] }
        },
        {
            content: getEvaluationResultText(evaluation),
            styles: { fillColor: [211, 211, 211], fontStyle: 'bold', lineColor: [0, 0, 0] }
        },
        ],
    ];

    const evaluationColumnStyles = {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 90 },
    };

    doc.autoTable({
        head: [evaluationColumns],
        body: evaluationTableData,
        startY: 15,
        margin: { horizontal: 10 },
        styles: {
        lineWidth: 0.05,
        lineColor: [169, 169, 169],
        fontSize: 8,
        },
        columnStyles: evaluationColumnStyles,
    });

    const mainTasksCount = context.filteredTasks.filter(task => task.manob === 'MT').length;
    const mainTasksCompletedCount = context.filteredTasks.filter(
        task => task.manob === 'MT' && task.completed !== null
    ).length;
    const passRate = context.product.pass_rate ?? 50;
    const minRequired = Math.ceil(mainTasksCount * (passRate / 100));

    const leftTableData = [
        [
        { content: 'Number of Main Tasks in this practical training element:' },
        { content: mainTasksCount },
        ],
        [
        { content: `Number of Main Tasks performed by the Trainee (${minRequired} mini to PASS):` },
        { content: mainTasksCompletedCount },
        ],
    ];

    const rightTableData = [
        [{ content: 'Trainee Signature:' }, { content: '' }],
        [{ content: 'Assessor Signature:' }, { content: '' }],
        [{ content: 'Date:' }, { content: getEvaluationDateText(evaluation), styles: { halign: 'center' } }],
    ];

    const leftTableColumnStyles = {
        0: { cellWidth: 120 },
        1: { cellWidth: 20 },
    };

    const rightTableColumnStyles = {
        0: { cellWidth: 40 },
        1: { cellWidth: 40 },
    };

    doc.autoTable({
        body: leftTableData,
        startY: doc.previousAutoTable.finalY + 5,
        margin: { horizontal: 10 },
        styles: {
        lineWidth: 0.05,
        lineColor: [169, 169, 169],
        fontSize: 8,
        },
        columnStyles: leftTableColumnStyles,
    });

    doc.autoTable({
        body: rightTableData,
        startY: doc.autoTable.previous.finalY + 5,
        margin: { horizontal: 10 },
        styles: {
        lineWidth: 0.05,
        lineColor: [169, 169, 169],
        fontSize: 8,
        minCellHeight: 10,
        },
        columnStyles: rightTableColumnStyles,
        didDrawCell: (data) => {
        if (data.column.index !== 1 || data.section !== 'body') return;

        if (data.row.index === 0) {
            if (evaluation?.trainee_signature) {
            const imageWidth = 33;
            const imageHeight = data.cell.height - 1;
            const imageX = data.cell.x + (data.cell.width - imageWidth) / 2;
            const imageY = data.cell.y + (data.cell.height - imageHeight) / 2;

            doc.addImage(
                evaluation.trainee_signature,
                'PNG',
                imageX,
                imageY,
                imageWidth,
                imageHeight
            );
            } else {
            doc.setFontSize(8);
            doc.text(
                'N/A',
                data.cell.x + data.cell.width / 2,
                data.cell.y + data.cell.height / 2 + 1,
                { align: 'center' }
            );
            }
        }

        if (data.row.index === 1) {
            if (evaluation?.assessor_signature) {
            const imageWidth = 33;
            const imageHeight = data.cell.height - 1;
            const imageX = data.cell.x + (data.cell.width - imageWidth) / 2;
            const imageY = data.cell.y + (data.cell.height - imageHeight) / 2;

            doc.addImage(
                evaluation.assessor_signature,
                'PNG',
                imageX,
                imageY,
                imageWidth,
                imageHeight
            );
            } else {
            doc.setFontSize(8);
            doc.text(
                'N/A',
                data.cell.x + data.cell.width / 2,
                data.cell.y + data.cell.height / 2 + 1,
                { align: 'center' }
            );
            }
        }
        }
    });
}

export function generateStandardLogbookPdf(context) {
    const doc = createLogbookDoc({ orientation: 'landscape' });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    drawHeader(doc, { title: context.logbookName });

    doc.text(`Trainee Information:`, 10, 35);
    doc.setFont('helvetica', 'normal'); 

    // Personal Information Section
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;

    // Single autoTable for trainee info
    doc.autoTable({
        startY: 40,
        head: [['Name', 'UIN', 'Place of Birth', 'Date of Birth', 'Date']],
        body: [[
            `${context.selectedTrainee.first_name} ${context.selectedTrainee.last_name}`,
            context.selectedTrainee.license_no,
            context.selectedTrainee.place_of_birth,
            context.selectedTrainee.date_of_birth,
            formattedDate,
        ]],
        margin: { left: 10, right: 10 },
        styles: INFO_TABLE_STYLE,
        headStyles: INFO_TABLE_HEAD_STYLE,
        columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 50 },
            2: { cellWidth: 60 },
            3: { cellWidth: 40 },
            4: { cellWidth: 45 },
        },
    });

    let currentY = doc.autoTable.previous?.finalY + 10 || 40;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Instructor(s) Information:', 10, currentY);
    currentY += 5;

    const instructorRows = context.instructors.map(instructor => [
        `${instructor.first_name} ${instructor.last_name}`,
        instructor.license_no,
        instructor.place_of_birth,
        instructor.date_of_birth,
        instructor.department
    ]);

    doc.autoTable({
        startY: currentY,
        head: [['Name', 'License No', 'Place of Birth', 'Date of Birth', 'Department']],
        body: instructorRows,
        margin: { left: 10, right: 10 },
        styles: INFO_TABLE_STYLE,
        headStyles: INFO_TABLE_HEAD_STYLE,
        columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 50 },
            2: { cellWidth: 60 },
            3: { cellWidth: 40 },
            4: { cellWidth: 45 },
        },
    });

    const courseStartY = doc.autoTable.previous.finalY + 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Course Information:', 10, courseStartY);

    const courseRow = [
        context.product.product_code,
        context.product.name,
        context.project.location,
        context.project.start_date,
        context.project.end_date
    ];

    doc.autoTable({
        startY: courseStartY + 5,
        head: [['Course Code', 'Course Name', 'Location', 'Start Date', 'End Date']],
        body: [courseRow],
        margin: { left: 10, right: 10 },
        styles: INFO_TABLE_STYLE,
        headStyles: INFO_TABLE_HEAD_STYLE,
        columnStyles: {
            0: { cellWidth: 35 },  // Course Code
            1: { cellWidth: 'auto' },  // Course Name
            2: { cellWidth: 50 },  // Location
            3: { cellWidth: 40 },  // Start Date
            4: { cellWidth: 40 },  // End Date
        },
    });


    doc.addPage();

    // **************************** TASKS ***********************************************

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`Tasks:`, 10, 12);
    doc.setFont('helvetica', 'normal');

    const columns = [
        { title: "ATA", dataKey: "ata" },
        { title: "Task No", dataKey: "task_no" },
        { title: "MT/AT", dataKey: "manob" },
        { title: "Scope", dataKey: "scope" },
        { title: "Description", dataKey: "description" },
        { title: "Reference", dataKey: "reference" },
        { title: "Location", dataKey: "location" },
        { title: "Trainee Signoff", dataKey: "completed" },
        { title: "Trainee Signature", dataKey: "t_signature" },
        { title: "Instructor SignOff", dataKey: "ins_so" },
        { title: "Inst Signature", dataKey: "i_signature" }
    ];

    // Group tasks by chapter number
    const groupedTasks = groupTasksByChapter(context.filteredTasks);

    // Loop through each chapter
    let startY = 18;
    let isFirstTable = true;
    const sortedChapters = sortChapterNumbers(Object.keys(groupedTasks));

    for (const chapter of sortedChapters) {
        let tableData = [];

        for (const task of groupedTasks[chapter]) {
            tableData.push({
                ata: task.chapt_no,
                task_no: task.task_no,
                manob: task.manob,
                scope: task.scope,
                description: task.description,
                reference: task.reference,
                location: task.entry_P2 === true
                    ? "Aircraft"
                    : task.entry_P1 === true
                    ? "Classroom"
                    : "",
                completed: task.completed,
                t_signature_id: task.t_signature_id || '',
                ins_so: task.ins_so,
                i_signature_id: task.ins_signature_id || '',
            });
        }
        // If there's not enough space on the current page, add a new page
        if (startY + tableData.length * 10 > doc.internal.pageSize.height - 20) {
            doc.addPage();
            startY = 10;
            isFirstTable = true;
        }

        // Determine table header: First table = column names, others = "Chapter: {chapt_no}"
        let tableHeader = isFirstTable
            ? [
                columns.map(col => {
                    if (col.dataKey === "ata" || col.dataKey === "task_no") {
                        return { ...col, styles: { halign: 'center' } };
                    }
                    return col;
                }),
                [{ content: getChapterName(context.chapters, chapter), colSpan: columns.length, styles: { halign: 'center', fontStyle: 'bold', fillColor: [64, 87, 132], textColor: [255, 255, 255] } }]
            ]
            : [
                [{ content: getChapterName(context.chapters, chapter), colSpan: columns.length, styles: { halign: 'center', fontStyle: 'bold', fillColor: [64, 87, 132], textColor: [255, 255, 255] } }]
            ];

        // Add the table to the pdf document
        doc.autoTable({
            startY: startY,
            head: tableHeader,
            columns: columns,
            body: tableData,
            margin: { horizontal: 10 },
            styles: {
                fontSize: 9,
                valign: 'middle',
            },
            columnStyles: {
                ata: { cellWidth: 10, halign: 'center' }, 
                task_no: { cellWidth: 11, halign: 'center' },
                manob: { cellWidth: 14 }, 
                scope: { cellWidth: 14 },
                description: { cellWidth: 'auto' },
                reference: { cellWidth: 27 },
                location: { cellWidth: 20 },
                completed: { cellWidth: 20 },
                t_signature: { cellWidth: 25 },
                ins_so: { cellWidth: 20 },
                i_signature: { cellWidth: 25 },
            },
            didDrawPage: (data) => {
                startY = data.cursor.y + 0;
            },
            didDrawCell: (data) => {
                // Instructor Signature
                if (data.column.dataKey === "i_signature" && data.row.raw.i_signature_id) {
                    const signatureId = data.row.raw.i_signature_id;
                    const selectedSignature = context.signatures.find(sig => sig.id === signatureId);
                    const imageData = selectedSignature ? selectedSignature.user_image : null;

                    if (imageData) {
                        const imageX = data.cell.x + 2;
                        const imageY = data.cell.y + 2;
                        const imageWidth = 18;
                        const imageHeight = 7;

                        doc.addImage(imageData, 'PNG', imageX, imageY, imageWidth, imageHeight);
                        doc.setLineWidth(0.25);
                        doc.setDrawColor(0, 0, 0);
                        doc.rect(imageX, imageY, imageWidth, imageHeight);
                    }
                }

                // Trainee Signature
                if (data.column.dataKey === "t_signature" && data.row.raw.t_signature_id) {
                    const signatureId = data.row.raw.t_signature_id;
                    const selectedSignature = context.signatures.find(sig => sig.id === signatureId);
                    const imageData = selectedSignature ? selectedSignature.user_image : null;

                    if (imageData) {
                        const imageX = data.cell.x + 2;
                        const imageY = data.cell.y + 2;
                        const imageWidth = 18;
                        const imageHeight = 7;

                        doc.addImage(imageData, 'PNG', imageX, imageY, imageWidth, imageHeight);
                        doc.setLineWidth(0.25);
                        doc.setDrawColor(0, 0, 0);
                        doc.rect(imageX, imageY, imageWidth, imageHeight);
                    }
                }
            }
        });
        startY = doc.autoTable.previous.finalY + 0;
        isFirstTable = false;
    }

    doc.addPage();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`Summary of Trainee Progress:`, 10, 12);
    doc.setFont('helvetica', 'normal');

    const head = [
        [
            { content: "Chapter", rowSpan: 2, styles: { halign: "left" } },
            { content: "Total Tasks", colSpan: 3 },
            { content: "Main Tasks", colSpan: 3 },
            { content: "Misc.", colSpan: 2 }
        ],
        [
            "Total",
            "# Complete",
            "% Complete",
            "Total",
            "# Complete",
            "% Complete",
            "Assigned",
            "Incomplete"
        ]
    ];

    const body = context.filteredChapters.map(chapter => {
        const total = chapter.tasks.all.length;
        const totalComplete = chapter.tasks.completed.length;

        const mainTotal = chapter.tasks.manob.length;
        const mainComplete = chapter.tasks.main_completed.length;

        const assigned = chapter.tasks.enabled.length;
        const incomplete = assigned - totalComplete;

        const pctTotal = total === 0 ? 100 : (totalComplete / total) * 100;
        const pctMain = mainTotal === 0 ? 100 : (mainComplete / mainTotal) * 100;
        return [
            `${chapter.chapt_no}: ${chapter.name}`,
            total,
            totalComplete,
            pctTotal.toFixed(1) + "%",
            mainTotal,
            mainComplete,
            pctMain.toFixed(1) + "%",
            assigned,
            incomplete
        ];
    });

    // Track totals row index
    const totalsRowIndex = body.length;

    // Add totals row
    body.push([
        "Overall",
        context.totals.totalTasks,
        context.totals.totalCompleted,
        context.totals.totalCompletePercentage,
        context.totals.totalMandatory,
        context.totals.totalMainCompleted,
        context.totals.totalManobPercentage,
        context.totals.totalEnabled,
        context.totals.totalOutstanding
    ]);
    const passRate = context.product.pass_rate ?? 50;

    // Add note row
    body.push([
        {
            content: `The percentage of main tasks completed must be > ${passRate}%`,
            colSpan: 9,
            styles: {
                fillColor: [220, 220, 220],
                textColor: 0,
                fontStyle: 'bold',
                lineWidth: 0.2,
                lineColor: [0, 0, 0]
            }
        }
    ]);

    doc.autoTable({
        head: head,
        body: body,
        startY: 18,
        styles: {
            lineColor: [0, 0, 0],
            fontSize: 9,
            cellPadding: 1,
            halign: "center",
            valign: "middle",
        },
        headStyles: {
            fillColor: [52, 73, 94],
            textColor: 255,
            fontStyle: "bold",
            lineColor: [0, 0, 0],
        },
        bodyStyles: {
            fillColor: [255, 255, 255]
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245]
        },
        columnStyles: {
            0: { halign: "left" }
        },
        willDrawPage: function () {
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.2);
        },
        didParseCell: function (data) {
            const isNoteRow = data.row.index === body.length - 1;
            if (isNoteRow) return;

            const isTotalsRow = data.row.index === totalsRowIndex;

            if (data.section === "head") {
                data.cell.styles.lineWidth = { bottom: 0.2 };
                data.cell.styles.lineColor = { bottom: [0, 0, 0] };
            }

            if ((data.section === "head" || data.section === "body") && data.column.index === 8) {
                data.cell.styles.lineWidth = { right: 0.2 };
                data.cell.styles.lineColor = { right: [0, 0, 0] };
            }

            if ((data.section === "head" || data.section === "body") && (data.column.index === 0 || data.column.index === 1 || data.column.index === 4 || data.column.index === 7)) {
                data.cell.styles.lineWidth = { left: 0.2 };
                data.cell.styles.lineColor = { left: [0, 0, 0] };
            }

            // Percentage highlighting for both normal and totals rows
            if (data.section === 'body' && (data.column.index === 3 || data.column.index === 6)) {
                const value = parseFloat(String(data.cell.raw).replace('%', ''));
                if (!isNaN(value)) {
                    if (value >=  passRate) {
                        data.cell.styles.fillColor = [198, 239, 206]; // light green
                        data.cell.styles.textColor = [0, 97, 0];
                    } else {
                        data.cell.styles.fillColor = [255, 235, 156]; // light orange
                        data.cell.styles.textColor = [156, 101, 0];
                    }
                }
            }

            // Totals row styling
            if (isTotalsRow) {
                data.cell.styles.lineWidth = 0.2;
                data.cell.styles.lineColor = [0, 0, 0];
                data.cell.styles.fontStyle = 'bold';

                // Keep gray fill for non-percentage cells
                if (!(data.column.index === 3 || data.column.index === 6)) {
                    data.cell.styles.fillColor = [220, 220, 220];
                    data.cell.styles.textColor = [0, 0, 0];
                }
            }
        }
    });
    doc.setDrawColor(0,0,0);
    doc.setLineWidth(0.2);

    doc.addPage();

    // ************************ ASSESSMENTS *********************************

    if (ENABLE_ASSESSMENT) {
        context.taskAssessments.forEach((taskAssessment, index) => {
            taskAssessment.assessments.forEach((assessment, subIndex) => {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.text(`Task Assessment: ${index + 1}, Attempt ${subIndex + 1}`, 10, 10);

                let startY = 18;
                doc.setFontSize(10);

                // Left Table: Basic Information
                doc.autoTable({
                    startY,
                    margin: { left: 10 },
                    body: [
                        ['ATA Chapter', taskAssessment.chapt_no],
                    ],
                    tableWidth: 'wrap',
                    padding: 2,
                    styles: {
                        font: 'Roboto-Regular',
                        lineWidth: 0.05,
                        lineColor: [169, 169, 169],
                        fontSize: 10,
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold'
                    },
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 227 }
                    },
                    didParseCell: function (data) {
                        // Check if the cell is in the first column
                        if (data.column.index === 0) {
                            data.cell.styles.font = 'Roboto-Bold';
                        }
                    }
                });

                const instructor = context.instructors.find(
                    (inst) => inst.user_id === assessment.instructor_id
                );
                doc.autoTable({
                    startY, 
                    startX: 110,
                    body: [
                        ['Assessor Name', instructor ? instructor.first_name + ' ' + instructor.last_name : 'Unknown'],
                    ],
                    margin: { left: 149 },
                    padding: 2,
                    tableWidth: 'wrap',
                    styles: {
                        font: 'Roboto-Regular',
                        lineWidth: 0.05,
                        lineColor: [169, 169, 169],
                        fontSize: 10,
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold'
                    },
                    columnStyles: {
                        0: { cellWidth: 50 }, // Field
                        1: { cellWidth: 88 }  // Information
                    },
                    didParseCell: function (data) {
                        // Check if the cell is in the first column
                        if (data.column.index === 0) {
                            data.cell.styles.font = 'Roboto-Bold';
                        }
                    }
                });

                startY = doc.lastAutoTable.finalY + 4;

                doc.autoTable({
                    startY,
                    head: [['Flight Deck Info', '', 'CMCS Fault']],
                    body: [
                        ['Logbook Entry', taskAssessment.logbook_entry === 1 ? 'X' : '', { content: taskAssessment.CMCS_fault, rowSpan: 4 }],
                        ['Flight Deck Effect', taskAssessment.flight_deck === 1 ? 'X' : '', ''],
                        ['Crew/Maintenance observation', taskAssessment.observation === 1 ? 'X' : '', ''],
                        [taskAssessment.extra_info, '', ''],
                    ],
                    margin: { left: 10 },
                    columnStyles: {
                        0: { cellWidth: 130 },
                        1: { cellWidth: 9 },
                        2: { cellWidth: 138 }
                    },
                    styles: {
                        font: 'Roboto-Regular',
                        fontSize: 10,
                    },
                    didParseCell: function (data) {
                        if (data.section === 'head') {
                            data.cell.styles.font = 'Roboto-Bold';
                        }
                        if (data.column.index === 0 || data.column.index === 1) {
                            data.cell.styles.font = 'Roboto-Bold';
                        }
                    },
                    didDrawCell: function (data) {
                        const doc = data.doc;
                        const { cell, table } = data;

                        const isFirstRow = data.row.index === 0;
                        const isLastRow = data.row.index === table.body.length - 1;
                        const isFirstCol = data.column.index === 0;
                        const isLastCol = data.column.index === table.columns.length - 1;

                        doc.setLineWidth(0.05);
                        doc.setDrawColor(169, 169, 169);

                        // Draw top border
                        if (isFirstRow) {
                            doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
                        }

                        // Draw bottom border
                        const isRowSpanCell = cell.rowSpan && cell.rowSpan > 1;
                        const reachesLastRow = isRowSpanCell && (data.row.index + cell.rowSpan - 1 === table.body.length - 1);

                        if (isLastRow || reachesLastRow) {
                            doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
                        }

                        // Draw left border
                        if (isFirstCol) {
                            doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
                        }

                        // Draw right border
                        if (isLastCol) {
                            doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);
                        }

                        // Draw vertical border between column 1 and column 2
                        if (data.column.index === 1) {
                            const colRightX = cell.x + cell.width;
                            doc.line(colRightX, cell.y, colRightX, cell.y + cell.height);
                        }
                        if (data.row.index === 0 && data.column.index === 1) {
                            const colRightX = cell.x + cell.width;
                            const colTopY = cell.y;

                            // Sum height of rows 0–3
                            let totalHeight = 0;
                            for (let i = 0; i < 4 && i < table.body.length; i++) {
                                totalHeight += table.body[i].height;
                            }

                            doc.line(colRightX, colTopY, colRightX, colTopY + totalHeight);
                        }
                    }
                });


                startY = doc.lastAutoTable.finalY + 4;

                const sections = [
                    { title: 'Questions:', value: taskAssessment.questions },
                    { title: 'Answers', value: assessment.answers },
                ];

                for (const { title, value } of sections) {
                    doc.autoTable({
                        head: [[title]],
                        body: [[{ content: value || '' }]],
                        startY,
                        margin: { horizontal: 10 },
                        styles: {
                            lineWidth: 0.05,
                            lineColor: [169, 169, 169],
                            fontSize: 10,
                        },
                        columnStyles: {
                            0: { cellWidth: 'auto' },
                        }
                    });
                    startY = doc.lastAutoTable.finalY + 4;
                }

                // Signature Table
                const rightTableData = [
                    ['Trainee Signature', ' trainee_sig'],
                    ['Assessor Signature', ' assessor_sig'],
                    ['Date:', assessment.date || ''],
                    [
                        { content: `Result:`, styles: { fillColor: [211, 211, 211], fontStyle: 'bold', lineColor: [0, 0, 0] } },
                        { content: assessment.result ? assessment.result.toUpperCase() : 'Not yet Evaluated', styles: { fillColor: [211, 211, 211], fontStyle: 'bold', lineColor: [0, 0, 0] } }
                    ],
                ];

                doc.autoTable({
                    body: rightTableData,
                    startY,
                    margin: { horizontal: 10 },
                    styles: {
                        lineWidth: 0.05,
                        lineColor: [169, 169, 169],
                        fontSize: 8,
                    },
                    columnStyles: {
                        0: { cellWidth: 40 },
                        1: { cellWidth: 40 },
                    },
                    didDrawCell: (data) => {
                        if (data.cell.raw === ' trainee_sig' && assessment.trainee_signature) {
                            doc.addImage(assessment.trainee_signature, 'PNG', data.cell.x + 1, data.cell.y + 0.5, 28, data.cell.height - 1);
                        }
                        if (data.cell.raw === ' assessor_sig' && assessment.assessor_signature) {
                            doc.addImage(assessment.assessor_signature, 'PNG', data.cell.x + 1, data.cell.y + 0.5, 28, data.cell.height - 1);
                        }
                    }
                });

                if (subIndex !== taskAssessment.assessments.length - 1 || index !== context.taskAssessments.length - 1) {
                    doc.addPage();
                }
            });
        });
    }

    // ************************************* EVALUATIONS *************************************************************

    if (context.continuousEvaluation) {
    addEvaluationSection(doc, context.continuousEvaluation, 'Continuous Evaluation', context);
    }

    if (context.remedialEvaluation) {
    doc.addPage();
    addEvaluationSection(doc, context.remedialEvaluation, 'Remedial Evaluation', context);
    }

    if (!context.continuousEvaluation && !context.remedialEvaluation) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Evaluation:', 10, 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('No submitted evaluation available.', 10, 20);
    }

    doc.addPage();

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 0, 0);
    const note_text = 'Note: Providing trainee and instructor declarations is optional. Additionally, the declarations provided are samples and can be easily changed to ones provided by the company.'
    doc.text(note_text, pageWidth / 2, 20, { align: 'center', maxWidth: pageWidth - 40 });
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(16);

    doc.text(`Declarations`, pageWidth / 2, 60, 'center');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    doc.setFont('helvetica', 'bold');
    doc.text(`Trainee Declaration:`, 20, 75);
    const text = `I acknowledge that I have successfully completed the assigned tasks in accordance with the provided instructions on the dates and times indicated in the logbook. I have made a diligent effort to meet the objectives and standards set for each task and acknowledge that each task is completed through my own work alone.`;
    doc.setFont('helvetica', 'normal');
    doc.text(text, 20, 81, { maxWidth: pageWidth - 30 });

    doc.setFont('helvetica', 'bold');
    doc.text(`Instructor Declaration:`, 20, 105);
    const text_inst = `I am signing off on behalf of all instructors that have contributed to this logbook. We have thoroughly assessed the trainee throughout their training. I ensured that each task was completed up to the required standard and in accordance with the provided instructions. If there were any discrepancies or issues, they were promptly addressed and resolved. The trainee has demonstrated competence and diligence throughout the training period.`;
    doc.setFont('helvetica', 'normal');
    doc.text(text_inst, 20, 111, { maxWidth: pageWidth - 30 });

    // Trainee Signature
    doc.text(`Trainee Signature:`, 15, pageHeight - 50);
    doc.line(47, pageHeight - 50, pageWidth / 2, pageHeight - 50); 

    doc.text(`Date:`, pageWidth - 85, pageHeight - 50)
    doc.line(pageWidth - 75, pageHeight - 50, pageWidth - 15, pageHeight - 50);

    // Instructor Signature
    doc.text(`Instructor Signature:`, 15, pageHeight - 30);
    doc.line(50, pageHeight - 30, pageWidth / 2, pageHeight - 30);

    doc.text(`Date:`, pageWidth - 85, pageHeight - 30)
    doc.line(pageWidth - 75, pageHeight - 30, pageWidth - 15, pageHeight - 30);

    // Assessor Signature
    doc.text(`Assessor Signature:`, 15, 280)
    doc.line(50, 280, 125, 280);

    doc.text(`Date:`, 150, 280)
    doc.line(160, 280, 195, 280);

    drawFooter(doc, { authorName: context.instructorName });

    savePdf(doc, context.logbookName);
}
